import { useEffect, useRef, useState } from 'react';
import { initializeMap } from '../MapInitializer';

export const useMapInitialization = () => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapError, setMapError] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const sourcesReadyRef = useRef(false);
  const cleanupFnRef = useRef(null);
  const isMountedRef = useRef(true);

  // Track component mount status
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initialize map
  useEffect(() => {
    // First clean up existing map
    if (map.current) {
      if (cleanupFnRef.current) {
        cleanupFnRef.current();
      }
      map.current.remove();
      map.current = null;
      setMapReady(false);
      sourcesReadyRef.current = false;
    }
    
    // Ensure component is still mounted
    if (!isMountedRef.current) {
      return;
    }
    
    const mapInitResult = initializeMap({
      mapContainer,
      setMapError,
      setMapReady,
      sourcesReadyRef,
      shouldShowDistanceLabels: true // This will be controlled by visibility later
    });
    
    if (mapInitResult) {
      map.current = mapInitResult.map;
      const resizeObserver = mapInitResult.resizeObserver;
      cleanupFnRef.current = mapInitResult.cleanup;
      
      return () => {
        if (mapContainer.current) {
          resizeObserver.unobserve(mapContainer.current);
        }
        
        if (cleanupFnRef.current) {
          cleanupFnRef.current();
        }
        
        if (map.current) {
          try {
            map.current.remove();
          } catch (error) {
            console.error('Error removing map:', error);
          }
          map.current = null;
          setMapReady(false);
          sourcesReadyRef.current = false;
        }
      };
    }
  }, []);

  return {
    mapContainer,
    map,
    mapError,
    mapReady,
    sourcesReadyRef,
    isMountedRef
  };
}; 